import { z } from 'zod'
import {
  crmCompanyFileKindSchema,
  crmCompanyFileSentStatusSchema,
  crmCompanyFileSentSummarySchema,
  crmCompanyFileSentViaSchema,
  crmCompanyReadSideEffectsSchema,
} from './crm-company-read'

export const CRM_FILES_SENT_READ_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every global CRM files-sent read alias. */
export const crmFilesSentReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  limit: optionalQueryString,
  cursor: optionalQueryString,
  companyId: optionalQueryString,
  opportunityId: optionalQueryString,
}).passthrough()

/**
 * The files-sent projection is shared with the company-scoped read, including
 * every metadata field and passthrough behavior. The CRM stores no bytes.
 */
export const crmFilesSentReadSideEffectsSchema = crmCompanyReadSideEffectsSchema
export const crmFileKindSchema = crmCompanyFileKindSchema
export const crmFileSentViaSchema = crmCompanyFileSentViaSchema
export const crmFileSentStatusSchema = crmCompanyFileSentStatusSchema
export const crmFileSentSummarySchema = crmCompanyFileSentSummarySchema

export const crmFilesSentListResponseSchema = z.object({
  contract_version: z.literal(CRM_FILES_SENT_READ_CONTRACT_VERSION),
  files: z.array(crmFileSentSummarySchema),
  nextCursor: z.string().nullable(),
  sideEffects: crmFilesSentReadSideEffectsSchema,
}).passthrough()

export const crmFileSentDetailResponseSchema = z.object({
  contract_version: z.literal(CRM_FILES_SENT_READ_CONTRACT_VERSION),
  file: crmFileSentSummarySchema,
  sideEffects: crmFilesSentReadSideEffectsSchema,
}).passthrough()

export const CRM_FILES_SENT_READ_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
] as const

export const crmFilesSentReadErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_FILES_SENT_READ_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }).passthrough(),
}).passthrough()

export type CrmFilesSentReadQuery = z.infer<typeof crmFilesSentReadQuerySchema>
export type CrmFilesSentReadSideEffects = z.infer<typeof crmFilesSentReadSideEffectsSchema>
export type CrmFileKind = z.infer<typeof crmFileKindSchema>
export type CrmFileSentVia = z.infer<typeof crmFileSentViaSchema>
export type CrmFileSentStatus = z.infer<typeof crmFileSentStatusSchema>
export type CrmFileSentSummary = z.infer<typeof crmFileSentSummarySchema>
export type CrmFilesSentListResponse = z.infer<typeof crmFilesSentListResponseSchema>
export type CrmFileSentDetailResponse = z.infer<typeof crmFileSentDetailResponseSchema>
export type CrmFilesSentReadErrorCode = (typeof CRM_FILES_SENT_READ_ERROR_CODES)[number]
export type CrmFilesSentReadErrorEnvelope = z.infer<typeof crmFilesSentReadErrorEnvelopeSchema>
